@if (visible) {
  <p-table
    (onFilter)="onFilter($event)"
    (selectionChange)="selectionChange.emit($event)"
    [columns]="columns"
    [dataKey]="dataKey"
    [loading]="loading"
    [paginator]="pagination"
    [rows]="rowsPerPage"
    [rowTrackBy]="getTrackByFn(dataKey)"
    [scrollable]="true"
    [selection]="selection"
    [showGridlines]="true"
    [sortField]="sortField"
    [sortOrder]="sortOrder"
    [stripedRows]="true"
    [value]="value"
    [virtualScroll]="!pagination"
    [virtualScrollDelay]="0"
    [virtualScrollItemSize]="49"
    [virtualScrollOptions]="{ numToleratedItems: 120 }"
    #dt
    scrollHeight="flex"
    size="small"
    styleClass="flex-table">
    <ng-template pTemplate="caption">
      <div [class.toolbar-single-row]="singleRowToolbar" class="toolbar">
        @if (hasFocusDate) {
          <p-datepicker
            (ngModelChange)="setFocusDate($event)"
            (onClearClick)="setFocusDate()"
            (onSelect)="setFocusDate($event)"
            [ngModel]="focusDate()"
            [ngModelOptions]="{ updateOn: 'blur' }"
            [readonlyInput]="!focusDateClearable"
            [showButtonBar]="focusDateClearable"
            [showIcon]="true"
            [style]="{ width: '140px' }"
            appendTo="body"
            dateFormat="dd.mm.yy"></p-datepicker>
        }

        @if (firstColumnFrozen && showFrozenColumnSelection) {
          <p-multiSelect
            [(ngModel)]="frozenColumns"
            (onChange)="frozenColumnsChange()"
            [options]="columns"
            [style]="{ minWidth: '300px' }"
            optionLabel="header"></p-multiSelect>
        }

        @if (hasFocusYear) {
          <p-select
            (ngModelChange)="focusYear.set($event)"
            [ngModel]="focusYear()"
            [options]="focusYearRange"
            [style]="{ width: '140px' }"
            appendTo="body"
            appScrollIntoView></p-select>
        }

        @for (button of toolbarButtons; track button.labelKey) {
          <p-button
            (onClick)="button.onClick.emit($event)"
            [disabled]="button.disabled"
            [icon]="button.icon"
            [label]="button.labelKey | translate"
            type="button"></p-button>
        }

        <ng-content select="[customAction]"></ng-content>

        @if (editable) {
          <p-button
            (onClick)="save()"
            [disabled]="isSaveDisabled"
            [label]="'ACTIONS.SAVE' | translate"
            icon="fal fa-save"
            type="button"></p-button>
          <p-button
            (onClick)="cancel()"
            [disabled]="isCancelDisabled"
            [label]="'ACTIONS.CANCEL' | translate"
            icon="fal fa-times"
            type="button"></p-button>
          @if (isCreateAllowed) {
            <p-button
              (onClick)="create()"
              [disabled]="isCreateDisabled"
              [label]="createLabel | translate"
              icon="fal fa-plus"
              type="button"></p-button>
          }
        }

        @if (selectable) {
          <p-button
            (onClick)="clearSelection()"
            [disabled]="selection?.length === 0"
            [label]="'ACTIONS.CLEAR_SELECTION' | translate: { count: selection.length }"
            icon="fal fa-square"
            type="button"></p-button>
        }

        <div class="spacer"></div>

        <p-button
          (onClick)="resetFilter()"
          [label]="'GLOBAL.RESET_FILTER' | translate"
          icon="fal fa-undo"
          iconPos="left"
          styleClass="p-button-outlined"
          type="button"></p-button>

        @if (rodixAdmin) {
          <p-button
            (onClick)="exportCSV(dt.filteredValue || dt.value)"
            icon="fal fa-file-alt"
            iconPos="left"
            label="CSV"
            styleClass="p-button-outlined"
            type="button"></p-button>

          <p-button
            (onClick)="exportExcel(dt.filteredValue || dt.value)"
            icon="fal fa-file-excel"
            iconPos="left"
            label="Excel"
            styleClass="p-button-outlined"
            type="button"></p-button>
        }
      </div>
    </ng-template>
    <ng-template let-columns pTemplate="header">
      @if (headerGroups?.length) {
        @for (groups of headerGroups; track $index) {
          <tr style="height: 35px">
            @if (selectable) {
              <th [style.flex-basis]="'50px'" [style.min-width]="'50px'" pFrozenColumn style="flex: 0"></th>
            }
            @for (group of groups; track $index) {
              <th
                [colSpan]="group.colSpan"
                [frozen]="group.frozen ?? false"
                [style.flex-basis]="(group.width ?? group.minWidth) + 'px'"
                [style.flex-grow]="group.width ? 0 : group.colSpan"
                [style.min-width]="group.width ? group.width + 'px' : group.minWidth + 'px'"
                [style.position]="group.frozen ? null : 'relative'"
                pFrozenColumn>
                @if (group.message) {
                  <div class="empty-columns-message">{{ group.title | translate }}</div>
                } @else if (columnSelectable && !group.frozen) {
                  <div
                    appEllipsisTooltip
                    class="header-ellipsis"
                    style="display: flex; align-items: center; gap: 0.5rem">
                    <p-checkbox
                      (ngModelChange)="selectedColumnsChange($event, group)"
                      [ngModel]="columnSelection"
                      [value]="group"></p-checkbox>
                    <label>{{ group.title }}</label>
                  </div>
                } @else {
                  <div appEllipsisTooltip class="header-ellipsis">{{ group.title }}</div>
                }
              </th>
            }
            @if (actionColumnWidthInPx) {
              <th
                [style.max-width]="actionColumnWidthInPx"
                [style.min-width]="actionColumnWidthInPx"
                [style.width]="actionColumnWidthInPx"></th>
            }
          </tr>
        }
      }
      <tr style="height: 35px">
        @if (selectable) {
          <th [frozen]="frozenColumns.length > 0" pFrozenColumn style="max-width: 50px; min-width: 50px; width: 50px">
            <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
          </th>
        }
        @for (col of columns; track col.field) {
          <th
            [frozen]="isColumnFrozen(col)"
            [style.max-width]="col.width"
            [style.min-width]="col.minWidth || col.width"
            [style.position]="isColumnFrozen(col) ? null : 'relative'"
            [style.width]="col.width"
            pFrozenColumn>
            @if (columnSelectable && !isColumnFrozen(col) && !col.disableSelection) {
              <div appEllipsisTooltip class="header-ellipsis" style="display: flex; align-items: center; gap: 0.5rem">
                <p-checkbox
                  (ngModelChange)="selectedColumnsChange($event, col)"
                  [ngModel]="columnSelection"
                  [value]="col"></p-checkbox>
                <label>{{ col.header }}</label>
              </div>
            } @else {
              <div appEllipsisTooltip class="header-ellipsis">{{ col.header }}</div>
            }
            <p-sortIcon
              (click)="dt.sort({ field: col.field })"
              [field]="col.field"
              [style]="{ position: 'absolute', right: '15px', top: '7px' }"></p-sortIcon>
          </th>
        }
        @if (actionColumnWidthInPx) {
          <th
            [style.max-width]="actionColumnWidthInPx"
            [style.min-width]="actionColumnWidthInPx"
            [style.width]="actionColumnWidthInPx"></th>
        }
      </tr>
      <tr style="height: 49px">
        @if (selectable) {
          <th [frozen]="frozenColumns.length > 0" pFrozenColumn style="max-width: 50px"></th>
        }
        @for (col of columns; track col.field) {
          <th
            [frozen]="isColumnFrozen(col)"
            [style.max-width]="col.width"
            [style.min-width]="col.minWidth || col.width"
            [style.width]="col.width"
            pFrozenColumn>
            @switch (col.filterType) {
              @case ('date') {
                <p-datepicker
                  (ngModelChange)="dt.filter($event, col.field, 'date')"
                  [ngModel]="getDateFilter(col.field)"
                  [showButtonBar]="true"
                  appendTo="body"
                  dateFormat="dd.mm.yy"></p-datepicker>
              }
              @case ('datetime') {
                <p-datepicker
                  (ngModelChange)="dt.filter($event, col.field, 'date')"
                  [ngModel]="getDateFilter(col.field)"
                  [showButtonBar]="true"
                  appendTo="body"
                  dateFormat="dd.mm.yy"></p-datepicker>
              }
              @case ('boolean') {
                <div style="width: 100%; text-align: center">
                  <p-checkbox
                    (onChange)="dt.filter($event.checked, col.field, 'true')"
                    [ngModel]="filters[col.field]?.value"></p-checkbox>
                </div>
              }
              @case ('percent') {
                <p-inputNumber
                  (onInput)="dt.filter($event.value ? toNumber($event) / 100 : null, col.field, 'equals')"
                  [max]="100"
                  [maxFractionDigits]="2"
                  [min]="0"
                  [minFractionDigits]="2"
                  [ngModel]="filters[col.field]?.value"
                  [showButtons]="false"
                  [suffix]="'%'"></p-inputNumber>
              }
              @case ('time') {
                <p-datepicker
                  (ngModelChange)="dt.filter($event, col.field, 'equals')"
                  [ngModel]="getDateFilter(col.field)"
                  [timeOnly]="true"
                  appendTo="body"></p-datepicker>
              }
              @case ('dateRangeAssignment') {
                <div style="width: 100%; text-align: center">
                  <p-checkbox
                    (onChange)="dt.filter($event.checked, col.field, 'hasValue')"
                    [ngModel]="filters[col.field]?.value"></p-checkbox>
                </div>
              }
              @case ('number') {
                <p-inputNumber
                  (onInput)="dt.filter($event.value, col.field, 'equals')"
                  [locale]="'de-CH'"
                  [minFractionDigits]="1"
                  [ngModel]="filters[col.field]?.value"
                  [useGrouping]="false"></p-inputNumber>
              }
              @case ('integer') {
                <p-inputNumber
                  (onInput)="dt.filter($event.value, col.field, 'equals')"
                  [locale]="'de-CH'"
                  [maxFractionDigits]="0"
                  [minFractionDigits]="0"
                  [ngModel]="filters[col.field]?.value"
                  [useGrouping]="false"></p-inputNumber>
              }
              @default {
                @if (col.options) {
                  <p-select
                    (onChange)="dt.filter($event.value, col.field, 'equals')"
                    [filter]="true"
                    [filterBy]="'label'"
                    [ngModel]="filters[col.field]?.value"
                    [options]="col.options"
                    [placeholder]="'-'"
                    [showClear]="true"
                    appendTo="body"
                    appScrollIntoView></p-select>
                } @else {
                  <input
                    (input)="dt.filter($any($event.target).value, col.field, 'contains')"
                    [ngModel]="getFilterValue(col.field)"
                    pInputText
                    type="text" />
                }
              }
            }
          </th>
        }
        @if (actionColumnWidthInPx) {
          <th
            [style.max-width]="actionColumnWidthInPx"
            [style.min-width]="actionColumnWidthInPx"
            [style.width]="actionColumnWidthInPx"></th>
        }
      </tr>
    </ng-template>
    <ng-template let-columns="columns" let-row let-rowData pTemplate="body">
      <tr [attr.aria-busy]="row?.deleting || null" [class.row-deleting]="row?.deleting" style="height: 49px">
        @if (selectable) {
          <td [frozen]="frozenColumns.length > 0" pFrozenColumn style="max-width: 50px; min-width: 50px; width: 50px">
            <p-tableCheckbox [value]="rowData"></p-tableCheckbox>
          </td>
        }
        @for (col of columns; track col.field) {
          <td
            [frozen]="isColumnFrozen(col)"
            [ngClass]="{ selected: col.selected && dt.isSelected(rowData) }"
            [style.max-width]="col.width"
            [style.min-width]="col.minWidth || col.width"
            [style.width]="col.width"
            [style]="row['_style_' + col.field]"
            pFrozenColumn>
            @if (col.filterType === 'html') {
              <div
                [innerHTML]="getData(row, '_html_' + col.field)"
                style="text-overflow: ellipsis; overflow: hidden"></div>
            } @else {
              <app-table-cell
                (valueChange)="update($event, row, col.field)"
                [changed]="row._changes && row._changes[col.field] !== undefined"
                [column]="col"
                [editable]="
                  col.editable &&
                  ((col.onlyInitialModificationAllowed && row._new) || !col.onlyInitialModificationAllowed)
                "
                [error]="row._errors && row._errors[col.field]"
                [options]="col.options"
                [required]="col.required"
                [route]="getRouterLink(col.route, row)"
                [row]="row"
                [type]="col.filterType"
                [url]="getUrl(row, col.url)"
                [value]="getData(row, col.field)"></app-table-cell>
            }
          </td>
        }
        @if (actionColumnWidthInPx) {
          <td
            [style.max-width]="actionColumnWidthInPx"
            [style.min-width]="actionColumnWidthInPx"
            [style.width]="actionColumnWidthInPx"
            style="white-space: nowrap">
            @if (editable) {
              @if (row.deletable === null || row.deletable === undefined || row.deletable === true) {
                <p-button
                  (onClick)="delete(row)"
                  [label]="'ACTIONS.DELETE' | translate"
                  [style]="{ 'margin-right': '0.5rem' }"
                  icon="fal fa-trash"
                  size="small"
                  styleClass="p-button-danger"
                  type="button"></p-button>
              }
            }
            @for (button of actionButtons; track button.labelKey) {
              @if (
                (!button.displayIf || row[button.displayIf]) && (!button.displayIfNot || !row[button.displayIfNot])
              ) {
                <p-button
                  (onClick)="button.onClick.emit({ event: $event, row: row })"
                  [disabled]="button.disabled"
                  [icon]="button.icon"
                  [label]="button.labelKey | translate"
                  [style]="{ 'margin-right': '0.5rem' }"
                  [styleClass]="'p-button-' + button.type + (button.type === 'primary' ? ' p-button-outlined' : '')"
                  size="small"
                  type="button"></p-button>
              }
            }
          </td>
        }
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      @if (!loading) {
        <tr>
          <td [attr.colspan]="columns.length + 2" class="table-empty-message">{{ 'GLOBAL.NO_ENTRIES' | translate }}</td>
        </tr>
      }
    </ng-template>

    @if (footer) {
      <ng-template let-columns pTemplate="footer">
        <tr>
          @for (col of columns; track col.field) {
            <td [style.max-width]="col.width" [style.min-width]="col.minWidth || col.width" [style.width]="col.width">
              @if (col.footerKey) {
                {{ col.footerKey | translate }}
              }
              @if (col.footer) {
                {{ col.footer() }}
              }
            </td>
          }
        </tr>
      </ng-template>
    }
  </p-table>
}
